import React, { Component } from 'react';
import { connect } from 'react-redux';
import { fetchMachineStatus, fetchUsersOnline } from '../actions/StatusActions';
import { getProductConfig } from '../backend';
import './StatusView.css';
import { statusVersionLabel, versionTag } from '../util/releaseVersion';

class StatusView extends Component {
    constructor(props) {
        super(props);
        this._pollTimer = null;
    }

    componentDidMount() {
        // Cloud product has no machine-status endpoint — don't poll it.
        if (!getProductConfig().pollsSystemStatus) return;
        this.fetchStatus();
        this._pollTimer = setInterval(() => this.fetchStatus(), 60000);
    }

    componentWillUnmount() {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.sSess !== this.props.sSess && this.props.sSess) {
            this.fetchStatus();
        }
    }

    fetchStatus() {
        const { sSess, dispatch } = this.props;
        if (!sSess) return;
        // Local NVR product only (also guards the componentDidUpdate sSess-change path).
        if (!getProductConfig().pollsSystemStatus) return;
        dispatch(fetchMachineStatus(sSess));
        dispatch(fetchUsersOnline(sSess));
    }

    render() {
        const { usersOnline, machineStatus, sVersion, sReleaseVersion, fReleaseCandidate, cameras, bSerial } = this.props;
        const nvrVersion = statusVersionLabel({ sVersion, sReleaseVersion, fReleaseCandidate });
        const appVersion = versionTag(process.env.REACT_APP_VERSION);

        // Render in both dview and eView modes — Java StatusController:113 only blocks
        // the polling timer in eView, not the panel itself. Status RPCs use the active
        // NVR's sSess so they return that NVR's data in either mode.
        //
        // Distinguish "not fetched yet" (null) from "fetched with zero" (e.g. [] or 0).
        // Render a non-breaking space when not loaded so the row reserves vertical
        // space and the layout doesn't jump when data arrives.
        const onlineLoaded = Array.isArray(usersOnline);
        const statusLoaded = machineStatus && machineStatus.sTotal && machineStatus.sUsed;
        const daysLoaded = machineStatus && typeof machineStatus.days === 'number';

        // Hide "N Days Rec" when the sSess NVR (controller in eView, active NVR in dview)
        // has no enabled cameras — "0 Days Rec" on a cam-less controller reads like the
        // system isn't recording when really there's nothing to record.
        const hasEnabledCams = Array.isArray(cameras) && cameras.some(c =>
            c && c.fEnable === true && (c.bSerial === undefined || c.bSerial === bSerial)
        );

        const onlineCount = onlineLoaded ? usersOnline.length : 0;
        const userList = onlineLoaded ? usersOnline.join(', ') : '';
        const days = daysLoaded ? machineStatus.days : 0;
        const daysLabel = days === 1 ? '1 Day Rec' : `${days} Days Rec`;

        return (
            <div className="status-view">
                <span
                    className="status-online"
                    title={userList ? `Online: ${userList}` : ''}
                >
                    {onlineLoaded ? `${onlineCount} Online` : ' '}
                </span>
                <span className="status-storage">
                    {statusLoaded
                        ? `${machineStatus.sUsed} / ${machineStatus.sTotal}${machineStatus.sPercent ? ` (${machineStatus.sPercent})` : ''}`
                        : ' '}
                </span>
                <span className="status-days">
                    {daysLoaded && hasEnabledCams ? daysLabel : ' '}
                </span>
                <span className="status-dvs-version">
                    {nvrVersion || ' '}
                </span>
                {appVersion && (
                    <span className="status-version">App {appVersion}</span>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    sSess: state.auth.sSess,
    usersOnline: state.server.usersOnline,
    machineStatus: state.server.machineStatus,
    sVersion: state.server.sVersion,
    sReleaseVersion: state.server.sReleaseVersion,
    fReleaseCandidate: state.server.fReleaseCandidate,
    cameras: state.server.cameras,
    bSerial: state.server.bSerial
});

export default connect(mapStateToProps)(StatusView);
